import { useEffect, useState } from "react"
import { Alert, Box, Chip, Stack, Typography } from "@mui/material"
import GenericDialog from "@cmp/genericDialog"

// An action that cannot be undone, and that reaches OUT of our database — a
// HubSpot pipeline stage, a Slack removal, a refund — deserves more than an
// "Are you sure?" that muscle memory clicks straight through.
//
// Two tiers, the same shape as the document-generation service's ConfirmDialog:
//   1. The first click ARMS the action. It does not perform it.
//   2. The confirm button then stays disabled for `countdownSec` seconds,
//      counting down in the open, so there is a beat in which to actually read
//      what is about to happen and back out.
//
// It also SHOWS the consequences instead of describing them vaguely — one line
// per thing that will change, marked by whether it leaves our database. That is
// what stops "offboard" from quietly meaning "mark this student as churned in
// HubSpot" without ever saying so.
export type ConfirmConsequence = {
  text: string
  // external = leaves our database (HubSpot, Slack, payments). Hard to undo.
  // internal = our own data, reversible.
  variant: "external" | "internal"
}

export function ArmedConfirmDialog(props: {
  open: boolean
  title: string
  message?: string
  consequences?: ConfirmConsequence[]
  footer?: string
  okLabel: string
  countdownSec?: number
  loading?: boolean
  // The last attempt failed and the dialog is still open. Disarms it, so a retry
  // cannot be a single instant click on a live destructive button.
  failed?: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const {
    open,
    title,
    message,
    consequences = [],
    footer,
    okLabel,
    countdownSec = 4,
    loading = false,
    failed = false,
    onConfirm,
    onCancel,
  } = props

  const [armed, setArmed] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(countdownSec)

  // Closing must reset both, or re-opening would hand back an already-armed
  // button whose countdown has long since elapsed — one click from firing.
  useEffect(() => {
    if (!open) {
      setArmed(false)
      setSecondsLeft(countdownSec)
    }
  }, [open, countdownSec])

  // A request that FAILED leaves the dialog open and still armed, with the
  // countdown long since run out — so the retry would be a single, instant click
  // on a live destructive button. Disarm it: a retry has to be deliberate too.
  useEffect(() => {
    if (failed) {
      setArmed(false)
      setSecondsLeft(countdownSec)
    }
  }, [failed, countdownSec])

  useEffect(() => {
    if (!armed || secondsLeft <= 0) {
      return
    }
    const timer = setTimeout(() => setSecondsLeft((left) => left - 1), 1000)
    return () => clearTimeout(timer)
  }, [armed, secondsLeft])

  const counting = armed && secondsLeft > 0

  return (
    <GenericDialog
      open={open}
      size="xs"
      title={title}
      onClose={onCancel}
      content={
        <Stack gap={2}>
          {message && <Typography variant="body2">{message}</Typography>}
          {consequences.length > 0 && (
            <Stack gap={1}>
              {consequences.map((consequence) => (
                <Box
                  key={consequence.text}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Chip
                    size="small"
                    label={consequence.variant}
                    color={
                      consequence.variant === "external" ? "error" : "default"
                    }
                    sx={{ width: "80px", flexShrink: 0 }}
                  />
                  <Typography variant="body2">{consequence.text}</Typography>
                </Box>
              ))}
            </Stack>
          )}
          {armed && (
            <Alert severity="warning">
              <Typography variant="body2">
                {footer ?? "This cannot be undone."}
              </Typography>
            </Alert>
          )}
        </Stack>
      }
      buttons={[
        {
          text: "Cancel",
          type: "secondary",
          onClick: onCancel,
          disabled: loading,
        },
        {
          // The first click only arms it; the second — once the countdown has
          // run out — is the one that actually does the thing.
          text: !armed
            ? okLabel
            : counting
            ? `Confirm in ${secondsLeft}…`
            : `Yes, ${okLabel.toLowerCase()}`,
          type: "danger",
          onClick: () => (armed ? onConfirm() : setArmed(true)),
          disabled: loading || counting,
          loading,
        },
      ]}
    />
  )
}
